import {
  ACTIVITY_TYPE_LABELS,
  ACTIVITY_TYPES,
  type ActivityItem,
  type ActivityType,
  activityInputSchema,
  type CompanyDetails,
  type ContactDetails,
  companyInputSchema,
  contactInputSchema,
  DEAL_SOURCE_LABELS,
  DEAL_SOURCES,
  type DealDetails,
  type DealSource,
  dealInputSchema,
  type ProductItem,
} from '@erp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Plus, Trash2 } from 'lucide-react';
import { type FormEvent, type ReactNode, useRef, useState } from 'react';
import { Dialog } from '@/components/dialog';
import { toLocalInput } from '@/components/task-bits';
import { Button, Field, Input, Select, Textarea } from '@/components/ui';
import { UserPicker } from '@/components/user-picker';
import { ApiError, api } from '@/lib/api';
import { useUser } from '@/lib/auth';
import { crmMetaQuery } from '@/lib/crm';
import { formatMoney } from '@/lib/format';
import { CompanyPicker, ContactPicker, fetchProducts, MultiInput, SearchPicker } from './crm-bits';

/** Сохранение формы: ошибки полей — под полями, остальные — общим сообщением. */
function useSave<T>(
  fn: (body: Record<string, unknown>) => Promise<T>,
  onDone: (res: T) => void | Promise<void>,
) {
  const qc = useQueryClient();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<ReactNode>(null);
  const m = useMutation({
    mutationFn: fn,
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: ['crm'] });
      await onDone(res);
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setErrors(err.fields);
        setFormError(Object.keys(err.fields).length && !err.fields._ ? null : (err.fields._ ?? err.message));
      } else setFormError(err.message);
    },
  });
  return { m, errors, setErrors, formError, setFormError };
}

function issuesToFields(issues: { path: PropertyKey[]; message: string }[]) {
  return Object.fromEntries(issues.map((i) => [String(i.path[0] ?? '_'), i.message]));
}

function FormError({ children }: { children: ReactNode }) {
  if (!children) return null;
  return (
    <p
      role="alert"
      className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-[13.5px] text-danger"
    >
      {children}
    </p>
  );
}

function Footer({
  onClose,
  loading,
  label = 'Сохранить',
}: {
  onClose: () => void;
  loading: boolean;
  label?: string;
}) {
  return (
    <div className="flex justify-end gap-2 border-t border-line pt-4">
      <Button variant="secondary" onClick={onClose}>
        Отмена
      </Button>
      <Button type="submit" loading={loading}>
        {label}
      </Button>
    </div>
  );
}

// ---------- Контакт ----------

export function ContactFormDialog({
  contact,
  defaultCompany,
  onClose,
  onCreated,
}: {
  contact?: ContactDetails;
  defaultCompany?: { id: string; label: string };
  onClose: () => void;
  /** Вместо перехода в карточку — вернуть созданный контакт (выбор из формы сделки) */
  onCreated?: (c: { id: string; label: string }) => void;
}) {
  const navigate = useNavigate();
  const me = useUser();
  const [company, setCompany] = useState<{ id: string; label: string } | null>(
    contact?.company ? { id: contact.company.id, label: contact.company.name } : (defaultCompany ?? null),
  );
  const [phones, setPhones] = useState<string[]>(contact?.phones ?? []);
  const [emails, setEmails] = useState<string[]>(contact?.emails ?? []);
  const [owner, setOwner] = useState<string[]>(
    contact ? (contact.owner ? [contact.owner.userId] : []) : [me.id],
  );
  const [duplicate, setDuplicate] = useState<string | null>(null);
  const createdName = useRef('');
  const { m, errors, setErrors, formError } = useSave(
    (body) =>
      contact
        ? api(`/crm/contacts/${contact.id}`, { method: 'PATCH', body }).then(() => ({ id: contact.id }))
        : api<{ id: string }>('/crm/contacts', { method: 'POST', body }),
    async (res) => {
      onClose();
      if (!contact && onCreated) onCreated({ id: res.id, label: createdName.current });
      else if (!contact) await navigate({ to: '/crm/contacts/$id', params: { id: res.id } });
    },
  );

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const body = {
      fullName: String(fd.get('fullName') ?? ''),
      position: String(fd.get('position') ?? ''),
      city: String(fd.get('city') ?? ''),
      notes: String(fd.get('notes') ?? ''),
      companyId: company?.id ?? null,
      phones,
      emails,
      ownerId: owner[0] ?? null,
    };
    const parsed = contactInputSchema.safeParse(body);
    if (!parsed.success) return setErrors(issuesToFields(parsed.error.issues));
    createdName.current = parsed.data.fullName;
    setDuplicate(null);
    m.mutate(body, {
      onError: (err) => {
        const id = err instanceof ApiError ? (err.data.duplicateId as string | undefined) : undefined;
        if (id) setDuplicate(id);
      },
    });
  }

  return (
    <Dialog title={contact ? contact.fullName : 'Новый клиент'} onClose={onClose} width={620}>
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4 px-5 py-5">
        <Field label="Имя" error={errors.fullName}>
          {(p) => (
            <Input
              {...p}
              name="fullName"
              defaultValue={contact?.fullName}
              placeholder="Фамилия Имя Отчество"
            />
          )}
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Телефоны" error={errors.phones}>
            {(p) => (
              <MultiInput
                id={p.id}
                invalid={p['aria-invalid']}
                value={phones}
                onChange={setPhones}
                type="tel"
                placeholder="+7 900 000-00-00"
              />
            )}
          </Field>
          <Field label="E-mail" error={errors.emails ?? errors['emails.0']}>
            {(p) => (
              <MultiInput
                id={p.id}
                invalid={p['aria-invalid']}
                value={emails}
                onChange={setEmails}
                type="email"
                placeholder="name@example.ru"
              />
            )}
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Компания" error={errors.companyId} hint="Необязательно">
            {(p) => (
              <CompanyPicker
                id={p.id}
                value={company}
                onChange={(c) => setCompany(c ? { id: c.id, label: c.name } : null)}
              />
            )}
          </Field>
          <Field label="Должность" error={errors.position}>
            {(p) => (
              <Input
                {...p}
                name="position"
                defaultValue={contact?.position ?? ''}
                placeholder="Главный механик"
              />
            )}
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Город" error={errors.city}>
            {(p) => <Input {...p} name="city" defaultValue={contact?.city ?? ''} />}
          </Field>
          <Field
            label="Ответственный"
            error={errors.ownerId}
            hint={owner.length ? undefined : 'Без ответственного — клиент общий'}
          >
            {(p) => <UserPicker id={p.id} value={owner} onChange={setOwner} />}
          </Field>
        </div>
        <Field label="Заметки" error={errors.notes}>
          {(p) => (
            <Textarea
              {...p}
              name="notes"
              rows={3}
              defaultValue={contact?.notes ?? ''}
              placeholder="Техника, предпочтения, договорённости"
            />
          )}
        </Field>
        <FormError>
          {formError}
          {duplicate && (
            <>
              {' '}
              <Link to="/crm/contacts/$id" params={{ id: duplicate }} className="link" onClick={onClose}>
                Открыть карточку
              </Link>
            </>
          )}
        </FormError>
        <Footer onClose={onClose} loading={m.isPending} />
      </form>
    </Dialog>
  );
}

// ---------- Компания ----------

export function CompanyFormDialog({
  company,
  onClose,
  onCreated,
}: {
  company?: CompanyDetails;
  onClose: () => void;
  onCreated?: (c: { id: string; label: string }) => void;
}) {
  const navigate = useNavigate();
  const me = useUser();
  const [phones, setPhones] = useState<string[]>(company?.phones ?? []);
  const [emails, setEmails] = useState<string[]>(company?.emails ?? []);
  const [owner, setOwner] = useState<string[]>(
    company ? (company.owner ? [company.owner.userId] : []) : [me.id],
  );
  const createdName = useRef('');
  const { m, errors, setErrors, formError } = useSave(
    (body) =>
      company
        ? api(`/crm/companies/${company.id}`, { method: 'PATCH', body }).then(() => ({ id: company.id }))
        : api<{ id: string }>('/crm/companies', { method: 'POST', body }),
    async (res) => {
      onClose();
      if (!company && onCreated) onCreated({ id: res.id, label: createdName.current });
      else if (!company) await navigate({ to: '/crm/companies/$id', params: { id: res.id } });
    },
  );

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const body = Object.fromEntries(
      ['name', 'inn', 'kpp', 'city', 'address', 'website', 'notes'].map((k) => [k, String(fd.get(k) ?? '')]),
    ) as Record<string, unknown>;
    Object.assign(body, { phones, emails, ownerId: owner[0] ?? null });
    const parsed = companyInputSchema.safeParse(body);
    if (!parsed.success) return setErrors(issuesToFields(parsed.error.issues));
    createdName.current = parsed.data.name;
    m.mutate(body);
  }

  return (
    <Dialog title={company ? company.name : 'Новая компания'} onClose={onClose} width={620}>
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4 px-5 py-5">
        <Field label="Название" error={errors.name}>
          {(p) => <Input {...p} name="name" defaultValue={company?.name} placeholder="ООО «Агротехника»" />}
        </Field>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="ИНН" error={errors.inn}>
            {(p) => <Input {...p} name="inn" inputMode="numeric" defaultValue={company?.inn ?? ''} />}
          </Field>
          <Field label="КПП" error={errors.kpp}>
            {(p) => <Input {...p} name="kpp" inputMode="numeric" defaultValue={company?.kpp ?? ''} />}
          </Field>
          <Field label="Город" error={errors.city}>
            {(p) => <Input {...p} name="city" defaultValue={company?.city ?? ''} />}
          </Field>
        </div>
        <Field label="Адрес" error={errors.address}>
          {(p) => <Input {...p} name="address" defaultValue={company?.address ?? ''} />}
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Телефоны" error={errors.phones}>
            {(p) => <MultiInput id={p.id} value={phones} onChange={setPhones} type="tel" />}
          </Field>
          <Field label="E-mail" error={errors.emails ?? errors['emails.0']}>
            {(p) => <MultiInput id={p.id} value={emails} onChange={setEmails} type="email" />}
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Сайт" error={errors.website}>
            {(p) => <Input {...p} name="website" defaultValue={company?.website ?? ''} />}
          </Field>
          <Field label="Ответственный" error={errors.ownerId}>
            {(p) => <UserPicker id={p.id} value={owner} onChange={setOwner} />}
          </Field>
        </div>
        <Field label="Заметки" error={errors.notes}>
          {(p) => <Textarea {...p} name="notes" rows={3} defaultValue={company?.notes ?? ''} />}
        </Field>
        <FormError>{formError}</FormError>
        <Footer onClose={onClose} loading={m.isPending} />
      </form>
    </Dialog>
  );
}

// ---------- Сделка ----------

export function DealFormDialog({
  deal,
  defaultContact,
  defaultCompany,
  onClose,
}: {
  deal?: DealDetails;
  defaultContact?: { id: string; label: string };
  defaultCompany?: { id: string; label: string };
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const me = useUser();
  const meta = useQuery(crmMetaQuery);
  const [contact, setContact] = useState<{ id: string; label: string } | null>(
    deal?.contact ? { id: deal.contact.id, label: deal.contact.fullName } : (defaultContact ?? null),
  );
  const [company, setCompany] = useState<{ id: string; label: string } | null>(
    deal?.company ? { id: deal.company.id, label: deal.company.name } : (defaultCompany ?? null),
  );
  const [owner, setOwner] = useState<string[]>(deal ? (deal.owner ? [deal.owner.userId] : []) : [me.id]);
  const [newContact, setNewContact] = useState(false);
  const { m, errors, setErrors, formError } = useSave(
    (body) =>
      deal
        ? api(`/crm/deals/${deal.id}`, { method: 'PATCH', body }).then(() => ({ id: deal.id }))
        : api<{ id: string }>('/crm/deals', { method: 'POST', body }),
    async (res) => {
      onClose();
      if (!deal) await navigate({ to: '/crm/deals/$id', params: { id: res.id } });
    },
  );

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const amount = String(fd.get('amount') ?? '')
      .replace(/\s/g, '')
      .replace(',', '.');
    const body: Record<string, unknown> = {
      title: String(fd.get('title') ?? ''),
      contactId: contact?.id ?? null,
      companyId: company?.id ?? null,
      directionId: (fd.get('directionId') as string) || null,
      ownerId: owner[0] ?? null,
      closeDate: (fd.get('closeDate') as string) || null,
      description: String(fd.get('description') ?? ''),
      source: fd.get('source') as DealSource,
    };
    if (!deal?.items.length) body.amount = amount === '' ? null : amount;
    const parsed = dealInputSchema.safeParse(body);
    if (!parsed.success) return setErrors(issuesToFields(parsed.error.issues));
    if (!contact && !company) return setErrors({ contactId: 'Выберите клиента — контакт или компанию' });
    m.mutate(body);
  }

  const directions = meta.data?.directions.filter((d) => d.isActive || d.id === deal?.direction?.id) ?? [];

  return (
    <Dialog title={deal ? `Сделка #${deal.number}` : 'Новая сделка'} onClose={onClose} width={660}>
      {newContact && (
        <ContactFormDialog
          defaultCompany={company ?? undefined}
          onClose={() => setNewContact(false)}
          onCreated={(c) => setContact(c)}
        />
      )}
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4 px-5 py-5">
        <Field label="Название" error={errors.title}>
          {(p) => (
            <Input
              {...p}
              name="title"
              defaultValue={deal?.title}
              placeholder="Например: форсунки на John Deere, 6 шт."
            />
          )}
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Контакт" error={errors.contactId}>
            {(p) => (
              <div>
                <ContactPicker
                  id={p.id}
                  invalid={p['aria-invalid']}
                  value={contact}
                  onChange={(c) => {
                    setContact(c ? { id: c.id, label: c.fullName } : null);
                    if (c?.company && !company) setCompany({ id: c.company.id, label: c.company.name });
                  }}
                />
                {!contact && (
                  <button type="button" className="link mt-1 text-[13px]" onClick={() => setNewContact(true)}>
                    + новый клиент
                  </button>
                )}
              </div>
            )}
          </Field>
          <Field label="Компания" error={errors.companyId} hint="Необязательно">
            {(p) => (
              <CompanyPicker
                id={p.id}
                value={company}
                onChange={(c) => setCompany(c ? { id: c.id, label: c.name } : null)}
              />
            )}
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Направление" error={errors.directionId}>
            {(p) => (
              <Select {...p} name="directionId" defaultValue={deal?.direction?.id ?? ''}>
                <option value="">Не выбрано</option>
                {directions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field
            label="Сумма, ₽"
            error={errors.amount}
            hint={deal?.items.length ? 'Считается по позициям' : undefined}
          >
            {(p) => (
              <Input
                {...p}
                name="amount"
                inputMode="decimal"
                disabled={Boolean(deal?.items.length)}
                defaultValue={deal?.amount ?? ''}
              />
            )}
          </Field>
          <Field label="Дата закрытия (план)" error={errors.closeDate}>
            {(p) => <Input {...p} type="date" name="closeDate" defaultValue={deal?.closeDate ?? ''} />}
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Ответственный"
            error={errors.ownerId}
            hint={owner.length ? undefined : 'Без ответственного — видят все'}
          >
            {(p) => <UserPicker id={p.id} value={owner} onChange={setOwner} />}
          </Field>
          <Field label="Источник" error={errors.source}>
            {(p) => (
              <Select {...p} name="source" defaultValue={deal?.source ?? 'manual'}>
                {DEAL_SOURCES.map((s) => (
                  <option key={s} value={s}>
                    {DEAL_SOURCE_LABELS[s]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
        <Field label="Комментарий" error={errors.description}>
          {(p) => (
            <Textarea
              {...p}
              name="description"
              rows={3}
              defaultValue={deal?.description ?? ''}
              placeholder="Техника, VIN, что нужно клиенту"
            />
          )}
        </Field>
        <FormError>{formError}</FormError>
        <Footer onClose={onClose} loading={m.isPending} label={deal ? 'Сохранить' : 'Создать сделку'} />
      </form>
    </Dialog>
  );
}

/** Причина отказа при переносе в стадию «провал». */
export function LostReasonDialog({
  onSubmit,
  onClose,
  loading,
}: {
  onSubmit: (reason: string) => void;
  onClose: () => void;
  loading: boolean;
}) {
  const [reason, setReason] = useState('');
  const presets = ['Дорого', 'Купили у конкурента', 'Нет в наличии', 'Не отвечает', 'Передумал'];
  return (
    <Dialog title="Почему сделка провалена" onClose={onClose} width={480}>
      <form
        className="flex flex-col gap-3 px-5 py-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (reason.trim()) onSubmit(reason.trim());
        }}
      >
        <div className="flex flex-wrap gap-1.5">
          {presets.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setReason(p)}
              className="rounded-full border border-line-strong px-2.5 py-1 text-[13px] hover:bg-sunken"
            >
              {p}
            </button>
          ))}
        </div>
        <Textarea
          aria-label="Причина"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="Причина отказа"
        />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" variant="danger" loading={loading} disabled={!reason.trim()}>
            Провалить сделку
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ---------- Позиции сделки ----------

interface ItemRow {
  key: string;
  productId: string | null;
  name: string;
  sku: string | null;
  qty: string;
  unit: string;
  price: string;
}

const num = (v: string) => Number(v.replace(/\s/g, '').replace(',', '.'));

export function DealItemsDialog({ deal, onClose }: { deal: DealDetails; onClose: () => void }) {
  const [rows, setRows] = useState<ItemRow[]>(
    deal.items.map((i) => ({
      key: i.id,
      productId: i.productId,
      name: i.name,
      sku: i.sku,
      qty: String(i.qty),
      unit: i.unit,
      price: String(i.price),
    })),
  );
  const { m, formError, setFormError } = useSave(
    (body) => api(`/crm/deals/${deal.id}/items`, { method: 'PUT', body }),
    () => onClose(),
  );
  const total = rows.reduce((s, r) => s + (num(r.qty) || 0) * (num(r.price) || 0), 0);
  const set = (key: string, patch: Partial<ItemRow>) =>
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const add = (p?: ProductItem) =>
    setRows((rs) => [
      ...rs,
      {
        key: crypto.randomUUID(),
        productId: p?.id ?? null,
        name: p?.name ?? '',
        sku: p?.sku ?? null,
        qty: '1',
        unit: p?.unit ?? 'шт',
        price: String(p?.price ?? ''),
      },
    ]);

  function save() {
    const items = rows.map((r) => ({
      productId: r.productId,
      name: r.name.trim(),
      sku: r.sku,
      qty: num(r.qty),
      unit: r.unit,
      price: num(r.price) || 0,
    }));
    if (items.some((i) => !i.name || !(i.qty > 0)))
      return setFormError('У каждой позиции нужны название и количество больше нуля');
    m.mutate({ items });
  }

  return (
    <Dialog title={`Товары и услуги — сделка #${deal.number}`} onClose={onClose} width={860}>
      <div className="flex flex-col gap-4 px-5 py-5">
        <div className="max-w-[420px]">
          <SearchPicker<ProductItem>
            value={null}
            onChange={(p) => p && add(p)}
            fetchItems={fetchProducts}
            cacheKey="products"
            placeholder="Добавить из справочника: артикул или название"
            renderItem={(p) => ({
              title: p.name,
              hint: [p.sku, formatMoney(p.price, 'RUB')].filter(Boolean).join(' · '),
            })}
          />
        </div>
        {rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-left text-[13.5px]">
              <thead>
                <tr className="text-[12.5px] text-steel">
                  <th className="pb-1.5 font-medium">Наименование</th>
                  <th className="w-[90px] pb-1.5 font-medium">Кол-во</th>
                  <th className="w-[70px] pb-1.5 font-medium">Ед.</th>
                  <th className="w-[120px] pb-1.5 font-medium">Цена, ₽</th>
                  <th className="w-[120px] pb-1.5 text-right font-medium">Сумма</th>
                  <th className="w-9" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} className="align-top">
                    <td className="py-1 pr-2">
                      <input
                        className="input"
                        aria-label="Наименование"
                        value={r.name}
                        onChange={(e) => set(r.key, { name: e.target.value })}
                      />
                      {r.sku && <span className="mt-0.5 block text-[12px] text-steel">{r.sku}</span>}
                    </td>
                    <td className="py-1 pr-2">
                      <input
                        className="input"
                        aria-label="Количество"
                        inputMode="decimal"
                        value={r.qty}
                        onChange={(e) => set(r.key, { qty: e.target.value })}
                      />
                    </td>
                    <td className="py-1 pr-2">
                      <input
                        className="input"
                        aria-label="Единица"
                        value={r.unit}
                        onChange={(e) => set(r.key, { unit: e.target.value })}
                      />
                    </td>
                    <td className="py-1 pr-2">
                      <input
                        className="input"
                        aria-label="Цена"
                        inputMode="decimal"
                        value={r.price}
                        onChange={(e) => set(r.key, { price: e.target.value })}
                      />
                    </td>
                    <td className="py-1 pt-3 text-right tabular-nums">
                      {formatMoney((num(r.qty) || 0) * (num(r.price) || 0), 'RUB')}
                    </td>
                    <td className="py-1">
                      <button
                        type="button"
                        aria-label="Удалить позицию"
                        onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))}
                        className="inline-flex size-9 items-center justify-center rounded-md text-steel hover:bg-sunken hover:text-danger"
                      >
                        <Trash2 className="size-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button variant="ghost" size="sm" onClick={() => add()}>
            <Plus className="size-4" aria-hidden />
            Строка вручную
          </Button>
          <p className="font-semibold tabular-nums">Итого: {formatMoney(total, 'RUB')}</p>
        </div>
        <FormError>{formError}</FormError>
        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button loading={m.isPending} onClick={save}>
            Сохранить
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ---------- Дело ----------

export function ActivityFormDialog({
  activity,
  dealId,
  contactId,
  onClose,
}: {
  activity?: ActivityItem;
  dealId?: string;
  contactId?: string;
  onClose: () => void;
}) {
  const me = useUser();
  const [type, setType] = useState<ActivityType>(activity?.type ?? 'call');
  const [responsible, setResponsible] = useState<string[]>([activity?.responsible.userId ?? me.id]);
  const tomorrow = new Date(Date.now() + 86_400_000);
  tomorrow.setHours(10, 0, 0, 0);
  const { m, errors, setErrors, formError } = useSave(
    (body) =>
      activity
        ? api(`/crm/activities/${activity.id}`, { method: 'PATCH', body })
        : api('/crm/activities', { method: 'POST', body }),
    () => onClose(),
  );

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const due = String(fd.get('dueAt') ?? '');
    const body = {
      type,
      subject: String(fd.get('subject') ?? ''),
      description: String(fd.get('description') ?? ''),
      dueAt: due ? new Date(due).toISOString() : '',
      responsibleId: responsible[0],
      ...(activity ? {} : { dealId: dealId ?? null, contactId: contactId ?? null }),
    };
    if (!activity) {
      const parsed = activityInputSchema.safeParse(body);
      if (!parsed.success) return setErrors(issuesToFields(parsed.error.issues));
    }
    if (activity) delete (body as Record<string, unknown>).type;
    m.mutate(body);
  }

  return (
    <Dialog title={activity ? 'Дело' : 'Запланировать'} onClose={onClose} width={520}>
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4 px-5 py-5">
        {!activity && (
          <fieldset className="flex gap-1.5">
            <legend className="sr-only">Тип</legend>
            {ACTIVITY_TYPES.map((t) => (
              <button
                key={t}
                type="button"
                aria-pressed={type === t}
                onClick={() => setType(t)}
                className={
                  type === t
                    ? 'h-8 rounded-md bg-graphite px-3 text-[13.5px] text-white'
                    : 'h-8 rounded-md border border-line-strong px-3 text-[13.5px] hover:bg-sunken'
                }
              >
                {ACTIVITY_TYPE_LABELS[t]}
              </button>
            ))}
          </fieldset>
        )}
        <Field label="Что сделать" error={errors.subject}>
          {(p) => (
            <Input
              {...p}
              name="subject"
              defaultValue={activity?.subject}
              placeholder={type === 'call' ? 'Перезвонить, уточнить VIN' : 'Отправить КП'}
            />
          )}
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Когда" error={errors.dueAt}>
            {(p) => (
              <Input
                {...p}
                type="datetime-local"
                name="dueAt"
                defaultValue={toLocalInput(activity?.dueAt ?? tomorrow.toISOString())}
              />
            )}
          </Field>
          <Field label="Ответственный" error={errors.responsibleId}>
            {(p) => (
              <UserPicker
                id={p.id}
                value={responsible}
                onChange={(v) => setResponsible(v.length ? v : [me.id])}
              />
            )}
          </Field>
        </div>
        <Field label="Подробности" error={errors.description}>
          {(p) => <Textarea {...p} name="description" rows={2} defaultValue={activity?.description ?? ''} />}
        </Field>
        <FormError>{formError ?? errors._}</FormError>
        <Footer onClose={onClose} loading={m.isPending} />
      </form>
    </Dialog>
  );
}
